import type { LucideIcon } from 'lucide-react';
import { Activity, Bell, CalendarDays, ClipboardCheck, Inbox, MessageCircle, Package } from 'lucide-react';
import { SES_NOTIFICATION_TYPES } from '@shared/ses/sesNotificationTypes';

export type NotificationInboxId =
  | 'all'
  | 'engineering'
  | 'suggestions'
  | 'ses'
  | 'assets'
  | 'leave'
  | 'reviews';

export type AppNotification = {
  id: string;
  title: string;
  body: string | null;
  notificationType: string;
  repositoryFullName?: string | null;
  readAt?: string | null;
  createdAt: string;
};

const ENGINEERING_TYPES = new Set([
  'inactive_contributor',
  'new_contributor_welcome',
  'streak',
  'main_branch_protection',
  'sensitive_data_detection',
  'repo_sync_failing',
]);

const SUGGESTION_TYPES = new Set(['new_suggestion', 'submitter_reply']);
const SES_TYPES = new Set<string>(Object.values(SES_NOTIFICATION_TYPES));
const ASSET_TYPES = new Set(['asset_assigned', 'asset_unassigned']);
const LEAVE_TYPES = new Set([
  'leave_application_submitted',
  'leave_application_approved',
  'leave_application_rejected',
]);
const REVIEW_TYPES = new Set(['review_cycle_invite', 'review_cycle_reminder']);

export const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  inactive_contributor: 'Inactive contributor',
  new_contributor_welcome: 'New contributor',
  streak: 'Streak milestone',
  main_branch_protection: 'Main branch commit',
  sensitive_data_detection: 'Sensitive data',
  repo_sync_failing: 'Sync failing',
  new_suggestion: 'New suggestion',
  submitter_reply: 'Suggestion activity',
  [SES_NOTIFICATION_TYPES.cycleInvite]: 'SES invite',
  [SES_NOTIFICATION_TYPES.cycleReminder]: 'SES reminder',
  asset_assigned: 'Asset assigned',
  asset_unassigned: 'Asset unassigned',
  leave_application_submitted: 'Leave submitted',
  leave_application_approved: 'Leave approved',
  leave_application_rejected: 'Leave rejected',
  review_cycle_invite: 'Review invite',
  review_cycle_reminder: 'Review reminder',
};

export const NOTIFICATION_INBOXES: {
  id: NotificationInboxId;
  label: string;
  description: string;
  icon: LucideIcon;
  match: (type: string) => boolean;
}[] = [
  {
    id: 'all',
    label: 'All',
    description: 'Everything in one place',
    icon: Inbox,
    match: () => true,
  },
  {
    id: 'engineering',
    label: 'Engineering',
    description: 'Repository and contributor alerts',
    icon: Bell,
    match: (type) => ENGINEERING_TYPES.has(type),
  },
  {
    id: 'suggestions',
    label: 'Suggestions',
    description: 'Suggestion box activity',
    icon: MessageCircle,
    match: (type) => SUGGESTION_TYPES.has(type),
  },
  {
    id: 'ses',
    label: 'SES',
    description: 'Stream Empowerment Sessions',
    icon: Activity,
    match: (type) => SES_TYPES.has(type),
  },
  {
    id: 'assets',
    label: 'Assets',
    description: 'Asset assignments and returns',
    icon: Package,
    match: (type) => ASSET_TYPES.has(type),
  },
  {
    id: 'leave',
    label: 'Leave',
    description: 'Leave application updates',
    icon: CalendarDays,
    match: (type) => LEAVE_TYPES.has(type),
  },
  {
    id: 'reviews',
    label: 'Reviews',
    description: 'Performance review invites and reminders',
    icon: ClipboardCheck,
    match: (type) => REVIEW_TYPES.has(type),
  },
];

export function inboxForNotificationType(type: string): NotificationInboxId {
  const inbox = NOTIFICATION_INBOXES.find((item) => item.id !== 'all' && item.match(type));
  return inbox?.id ?? 'engineering';
}

export function inboxLabelForType(type: string): string {
  const inbox = NOTIFICATION_INBOXES.find(
    (item) => item.id === inboxForNotificationType(type)
  );
  return inbox?.label ?? 'Notification';
}

export function notificationTypesForInbox(
  inboxId: NotificationInboxId
): string[] | undefined {
  switch (inboxId) {
    case 'engineering':
      return [...ENGINEERING_TYPES];
    case 'suggestions':
      return [...SUGGESTION_TYPES];
    case 'ses':
      return [...SES_TYPES];
    case 'assets':
      return [...ASSET_TYPES];
    case 'leave':
      return [...LEAVE_TYPES];
    case 'reviews':
      return [...REVIEW_TYPES];
    default:
      return undefined;
  }
}

export function filterNotificationsByInbox(
  notifications: AppNotification[],
  inboxId: NotificationInboxId
): AppNotification[] {
  const inbox = NOTIFICATION_INBOXES.find((item) => item.id === inboxId);
  if (!inbox || inboxId === 'all') return notifications;
  return notifications.filter((n) => inbox.match(n.notificationType));
}

export function countUnreadInInbox(
  notifications: AppNotification[],
  inboxId: NotificationInboxId
): number {
  return filterNotificationsByInbox(
    notifications.filter((n) => !n.readAt),
    inboxId
  ).length;
}

function assetDetailPathFromBody(body: string | null | undefined) {
  if (!body) return null;
  const match = body.match(/\/asset-tracking\/assets\/([^/\s]+)\/edit/);
  return match ? `/asset-tracking/assets/${match[1]}/edit` : null;
}

function reviewPathFromBody(body: string | null | undefined) {
  if (!body) return null;
  const portalMatch = body.match(/\/review\/cycles\/([^/\s]+)\/my-surveys/);
  if (portalMatch) return `/review/cycles/${portalMatch[1]}/my-surveys`;
  const tokenMatch = body.match(/\/review\/respond\?token=([^\s]+)/);
  if (tokenMatch) return `/review/respond?token=${tokenMatch[1]}`;
  return null;
}

export function getNotificationNavigatePath(n: AppNotification): string | null {
  const { notificationType: type } = n;

  if (SES_TYPES.has(type)) return '/ses/me';
  if (ASSET_TYPES.has(type)) return assetDetailPathFromBody(n.body);
  if (REVIEW_TYPES.has(type)) return reviewPathFromBody(n.body);
  if (SUGGESTION_TYPES.has(type)) return '/suggestion-box';
  if (type === 'leave_application_submitted') return '/leave-applications/admin';
  if (type === 'leave_application_approved' || type === 'leave_application_rejected') {
    return '/leave-applications';
  }

  return null;
}

export function notificationTypeLabel(type: string): string {
  return NOTIFICATION_TYPE_LABELS[type] ?? type.replace(/_/g, ' ');
}

export function formatNotificationDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
  });
}
